let fallbackDraftIdCounter = 0;
let fallbackDraftBeingIdCounter = 0;

export const POPULATION_TRANCHES = [
  { key: 'notables', label: 'Notables' },
  { key: 'notable-households', label: 'Notable Households' },
  { key: 'other-households', label: 'Other Households' },
  { key: 'unattached-residents', label: 'Unattached Residents' },
];

export const HOUSEHOLD_MANIFEST_TRANCHES = [
  'notable-households',
  'other-households',
];

const populationTrancheKeys = new Set(POPULATION_TRANCHES.map(({ key }) => key));
const householdManifestTrancheKeys = new Set(HOUSEHOLD_MANIFEST_TRANCHES);

export const isHouseholdManifestTranche = (value) =>
  householdManifestTrancheKeys.has(normalizePopulationTranche(value));

export const normalizeHouseholdManifest = (manifest) => {
  const populationTranche = normalizePopulationTranche(manifest?.populationTranche);
  if (!householdManifestTrancheKeys.has(populationTranche)) {
    return null;
  }

  return {
    populationTranche,
    targetCount: Math.max(0, Math.floor(Number(manifest?.targetCount) || 0)),
    households: (Array.isArray(manifest?.households) ? manifest.households : [])
      .map((household) => ({
        householdKey: String(household?.householdKey ?? '').trim(),
        householdName: String(household?.householdName ?? '').trim(),
        homePlaceId: String(household?.homePlaceId ?? '').trim(),
        anchorResidentNames: (Array.isArray(household?.anchorResidentNames)
          ? household.anchorResidentNames
          : [])
          .map((name) => String(name ?? '').trim())
          .filter(Boolean),
        householdConcept: String(household?.householdConcept ?? '').trim(),
        memberSlots: (Array.isArray(household?.memberSlots)
          ? household.memberSlots
          : [])
          .map((slot) => ({
            slotId: String(slot?.slotId ?? '').trim(),
            name: String(slot?.name ?? '').trim(),
            householdRole: String(slot?.householdRole ?? '').trim(),
            lifeStage: String(slot?.lifeStage ?? '').trim(),
            relationshipTargetName: String(slot?.relationshipTargetName ?? '').trim(),
            relationshipType: String(slot?.relationshipType ?? '').trim(),
            relationshipDetail: String(slot?.relationshipDetail ?? '').trim(),
            residentConcept: String(slot?.residentConcept ?? '').trim(),
          }))
          .filter((slot) => slot.slotId),
      }))
      .filter((household) => household.householdKey),
  };
};

export const normalizeHouseholdManifests = (manifests) =>
  Object.fromEntries(
    HOUSEHOLD_MANIFEST_TRANCHES
      .map((tranche) => [
        tranche,
        normalizeHouseholdManifest(manifests?.[tranche]),
      ])
      .filter(([, manifest]) => manifest)
  );

export const invalidateHouseholdManifestsFrom = (manifests, populationTranche) => {
  const normalizedTranche = normalizePopulationTranche(populationTranche);
  const trancheIndex = POPULATION_TRANCHES.findIndex(
    ({ key }) => key === normalizedTranche
  );
  if (trancheIndex < 0) {
    return normalizeHouseholdManifests(manifests);
  }

  const invalidatedTranches = new Set(
    POPULATION_TRANCHES
      .slice(trancheIndex)
      .map(({ key }) => key)
      .filter((key) => isHouseholdManifestTranche(key))
  );

  return Object.fromEntries(
    Object.entries(normalizeHouseholdManifests(manifests))
      .filter(([key]) => !invalidatedTranches.has(key))
  );
};

export const getHouseholdManifestSlots = (manifest) => {
  const normalizedManifest = normalizeHouseholdManifest(manifest);
  return (normalizedManifest?.households ?? []).flatMap((household) =>
    household.memberSlots.map((slot) => ({
      ...slot,
      householdKey: household.householdKey,
      householdName: household.householdName,
      homePlaceId: household.homePlaceId,
      anchorResidentNames: household.anchorResidentNames,
    }))
  );
};

export const getHouseholdManifestProgress = (manifest, suggestions) => {
  const normalizedManifest = normalizeHouseholdManifest(manifest);
  const generatedSlotIds = new Set(
    (Array.isArray(suggestions) ? suggestions : [])
      .map((suggestion) => String(suggestion?.populationPlanSlotId ?? '').trim())
      .filter(Boolean)
  );
  const households = (normalizedManifest?.households ?? []).map((household) => {
    const generatedCount = household.memberSlots
      .filter((slot) => generatedSlotIds.has(slot.slotId))
      .length;
    return {
      ...household,
      generatedCount,
      targetCount: household.memberSlots.length,
      complete: generatedCount >= household.memberSlots.length,
    };
  });

  return {
    households,
    generatedCount: households.reduce(
      (total, household) => total + household.generatedCount,
      0
    ),
    targetCount: households.reduce(
      (total, household) => total + household.targetCount,
      0
    ),
  };
};

export const getNextHouseholdSlotBatch = (
  manifest,
  suggestions,
  batchSize = 8
) => {
  const generatedSlotIds = new Set(
    (Array.isArray(suggestions) ? suggestions : [])
      .map((suggestion) => String(suggestion?.populationPlanSlotId ?? '').trim())
      .filter(Boolean)
  );
  const normalizedBatchSize = Math.max(1, Math.floor(Number(batchSize) || 1));

  return getHouseholdManifestSlots(manifest)
    .filter((slot) => !generatedSlotIds.has(slot.slotId))
    .slice(0, normalizedBatchSize);
};

export const normalizePopulationTranche = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return populationTrancheKeys.has(normalized) ? normalized : '';
};

export const normalizeResidentTranchePlan = (plan) =>
  (Array.isArray(plan) ? plan : [])
    .map((entry) => ({
      tranche: normalizePopulationTranche(entry?.tranche),
      expectedCount: Math.max(0, Math.floor(Number(entry?.expectedCount) || 0)),
      description: String(entry?.description ?? '').trim(),
    }))
    .filter((entry) => entry.tranche);

export const getResidentTranchePlanTotal = (plan) =>
  normalizeResidentTranchePlan(plan)
    .reduce((total, entry) => total + entry.expectedCount, 0);

export const getPopulationTrancheCounts = (suggestions) => {
  const counts = Object.fromEntries(POPULATION_TRANCHES.map(({ key }) => [key, 0]));

  (Array.isArray(suggestions) ? suggestions : []).forEach((suggestion) => {
    const tranche = normalizePopulationTranche(suggestion?.populationTranche);
    if (tranche) {
      counts[tranche] += 1;
    }
  });

  return counts;
};

export const isResidentTranchePlanValid = (plan, plannedPopulation) => {
  const normalizedPlan = normalizeResidentTranchePlan(plan);
  const tranches = new Set(normalizedPlan.map(({ tranche }) => tranche));
  const normalizedPopulation = Math.max(0, Math.floor(Number(plannedPopulation) || 0));

  return (
    normalizedPlan.length === POPULATION_TRANCHES.length &&
    tranches.size === POPULATION_TRANCHES.length &&
    POPULATION_TRANCHES.every(({ key }) => tranches.has(key)) &&
    getResidentTranchePlanTotal(normalizedPlan) === normalizedPopulation
  );
};

export const canGeneratePopulationTranche = (tranche, plan, suggestions) => {
  const normalizedTranche = normalizePopulationTranche(tranche);
  const normalizedPlan = normalizeResidentTranchePlan(plan);
  const counts = getPopulationTrancheCounts(suggestions);
  const trancheIndex = POPULATION_TRANCHES.findIndex(({ key }) => key === normalizedTranche);

  if (trancheIndex < 0) {
    return false;
  }

  for (let index = 0; index < trancheIndex; index += 1) {
    const priorTranche = POPULATION_TRANCHES[index].key;
    const priorTarget = normalizedPlan.find(({ tranche: key }) => key === priorTranche)?.expectedCount ?? 0;
    if (counts[priorTranche] < priorTarget) {
      return false;
    }
  }

  const target = normalizedPlan.find(({ tranche: key }) => key === normalizedTranche)?.expectedCount ?? 0;
  return counts[normalizedTranche] < target;
};

export const getNextPopulationBatchRequest = (
  plan,
  suggestions,
  {
    populationTranche = '',
    batchSize = 8,
    householdManifests = {},
  } = {}
) => {
  const normalizedPlan = normalizeResidentTranchePlan(plan);
  const normalizedTranche = normalizePopulationTranche(populationTranche);
  const normalizedBatchSize = Math.max(1, Math.floor(Number(batchSize) || 1));
  const counts = getPopulationTrancheCounts(suggestions);
  const candidateTranches = normalizedTranche
    ? [normalizedTranche]
    : POPULATION_TRANCHES.map(({ key }) => key);

  for (const tranche of candidateTranches) {
    if (!canGeneratePopulationTranche(tranche, normalizedPlan, suggestions)) {
      continue;
    }

    const target = normalizedPlan.find(({ tranche: key }) => key === tranche)?.expectedCount ?? 0;
    const currentCount = counts[tranche] ?? 0;
    if (isHouseholdManifestTranche(tranche)) {
      const manifest = normalizeHouseholdManifest(householdManifests?.[tranche]);
      if (!manifest) {
        return {
          populationTranche: tranche,
          requestedCount: 0,
          currentCount,
          targetCount: target,
          requiresHouseholdManifest: true,
          requestedHouseholdSlots: [],
        };
      }

      const requestedHouseholdSlots = getNextHouseholdSlotBatch(
        manifest,
        suggestions,
        normalizedBatchSize
      );
      return {
        populationTranche: tranche,
        requestedCount: requestedHouseholdSlots.length,
        currentCount,
        targetCount: target,
        requiresHouseholdManifest: false,
        requestedHouseholdSlots,
      };
    }

    return {
      populationTranche: tranche,
      requestedCount: Math.min(normalizedBatchSize, Math.max(0, target - currentCount)),
      currentCount,
      targetCount: target,
      requiresHouseholdManifest: false,
      requestedHouseholdSlots: [],
    };
  }

  return null;
};

export const generatePopulationBatchesSerially = async ({
  plan,
  suggestions = [],
  populationTranche = '',
  batchSize = 8,
  householdManifests = {},
  requestBatch,
  requestHouseholdManifest,
  onBatch,
  onHouseholdManifest,
  shouldContinue,
  pauseForHouseholdReview = true,
} = {}) => {
  if (typeof requestBatch !== 'function') {
    throw new Error('A population batch request function is required.');
  }

  let currentSuggestions = Array.isArray(suggestions) ? suggestions : [];
  let currentHouseholdManifests = normalizeHouseholdManifests(householdManifests);
  let batchCount = 0;
  let stopped = false;
  let pausedForManifest = false;
  let plannedManifestTranche = '';

  while (true) {
    if (typeof shouldContinue === 'function' && !shouldContinue()) {
      stopped = true;
      break;
    }

    const request = getNextPopulationBatchRequest(plan, currentSuggestions, {
      populationTranche,
      batchSize,
      householdManifests: currentHouseholdManifests,
    });
    if (!request) {
      break;
    }
    if (request.requiresHouseholdManifest) {
      if (typeof requestHouseholdManifest !== 'function') {
        throw new Error(
          `A household manifest is required for ${request.populationTranche}.`
        );
      }

      const manifest = normalizeHouseholdManifest(
        await requestHouseholdManifest({
          ...request,
          currentSuggestions,
          householdManifests: currentHouseholdManifests,
        })
      );
      if (!manifest || manifest.populationTranche !== request.populationTranche) {
        throw new Error(
          `Household planning did not return a valid ${request.populationTranche} manifest.`
        );
      }

      currentHouseholdManifests = {
        ...currentHouseholdManifests,
        [request.populationTranche]: manifest,
      };
      pausedForManifest = pauseForHouseholdReview;
      plannedManifestTranche = request.populationTranche;

      if (typeof onHouseholdManifest === 'function') {
        await onHouseholdManifest({
          ...request,
          manifest,
          householdManifests: currentHouseholdManifests,
          suggestions: currentSuggestions,
        });
      }
      if (pauseForHouseholdReview) break;
      continue;
    }
    if (request.requestedCount < 1) {
      throw new Error(
        `Population generation has no unfilled slots in ${request.populationTranche}.`
      );
    }

    const returnedSuggestions = await requestBatch({
      ...request,
      currentSuggestions,
      batchNumber: batchCount + 1,
    });
    const normalizedReturnedSuggestions = Array.isArray(returnedSuggestions)
      ? returnedSuggestions
      : [];
    if (request.requestedHouseholdSlots.length > 0) {
      const expectedSlotIds = new Set(
        request.requestedHouseholdSlots.map((slot) => slot.slotId)
      );
      const returnedSlotIds = new Set(
        normalizedReturnedSuggestions
          .map((suggestion) =>
            String(suggestion?.populationPlanSlotId ?? '').trim()
          )
          .filter(Boolean)
      );
      if (
        returnedSlotIds.size !== expectedSlotIds.size ||
        [...expectedSlotIds].some((slotId) => !returnedSlotIds.has(slotId))
      ) {
        throw new Error(
          `Population generation did not return every assigned household slot.`
        );
      }
    }
    const nextSuggestions = mergeInhabitantSuggestionBatches(
      currentSuggestions,
      normalizedReturnedSuggestions,
      { populationTranche: request.populationTranche }
    );
    const nextCount = getPopulationTrancheCounts(nextSuggestions)[request.populationTranche] ?? 0;

    if (nextCount <= request.currentCount) {
      throw new Error(
        `Population generation made no progress in ${request.populationTranche}.`
      );
    }

    currentSuggestions = nextSuggestions;
    batchCount += 1;

    if (typeof onBatch === 'function') {
      await onBatch({
        ...request,
        returnedSuggestions: normalizedReturnedSuggestions,
        suggestions: currentSuggestions,
        updatedTrancheCount: nextCount,
        batchNumber: batchCount,
      });
    }
  }

  return {
    suggestions: currentSuggestions,
    householdManifests: currentHouseholdManifests,
    batchCount,
    stopped,
    pausedForManifest,
    plannedManifestTranche,
  };
};

export const createDraftPlaceId = () => {
  const randomUuid = typeof window !== 'undefined'
    ? window.crypto?.randomUUID?.()
    : '';
  if (randomUuid) {
    return `draft-place:${randomUuid}`;
  }

  fallbackDraftIdCounter += 1;
  return `draft-place:${Date.now().toString(36)}-${fallbackDraftIdCounter.toString(36)}`;
};

export const ensureDraftPlaceIds = (suggestions, createId = createDraftPlaceId) => {
  const usedIds = new Set();

  return (Array.isArray(suggestions) ? suggestions : []).map((suggestion) => {
    let draftPlaceId = String(suggestion?.draftPlaceId ?? '').trim();

    while (!draftPlaceId || usedIds.has(draftPlaceId)) {
      draftPlaceId = String(createId()).trim();
    }

    usedIds.add(draftPlaceId);
    return {
      ...suggestion,
      draftPlaceId,
    };
  });
};

export const createDraftBeingId = () => {
  const randomUuid = typeof window !== 'undefined'
    ? window.crypto?.randomUUID?.()
    : '';
  if (randomUuid) {
    return `draft-being:${randomUuid}`;
  }

  fallbackDraftBeingIdCounter += 1;
  return `draft-being:${Date.now().toString(36)}-${fallbackDraftBeingIdCounter.toString(36)}`;
};

export const ensureDraftBeingIds = (
  suggestions,
  createId = createDraftBeingId
) => {
  const usedIds = new Set();

  return (Array.isArray(suggestions) ? suggestions : []).map((suggestion) => {
    let draftBeingId = String(suggestion?.draftBeingId ?? '').trim();

    while (!draftBeingId || usedIds.has(draftBeingId)) {
      draftBeingId = String(createId()).trim();
    }

    usedIds.add(draftBeingId);
    return {
      ...suggestion,
      draftBeingId,
    };
  });
};

const normalizePlaceSuggestionName = (value) =>
  String(value ?? '').trim().toLowerCase();

export const orderChildPlaceSuggestionEntriesByHierarchy = (entries) => {
  const pending = (Array.isArray(entries) ? entries : []).map((entry) => ({ ...entry }));
  const batchNames = new Set(
    pending
      .map((entry) => normalizePlaceSuggestionName(entry?.suggestion?.name))
      .filter(Boolean)
  );
  const emittedNames = new Set();
  const ordered = [];

  while (pending.length > 0) {
    const readyIndex = pending.findIndex((entry) => {
      const parentName = normalizePlaceSuggestionName(
        entry?.suggestion?.parentSuggestionName
      );
      return !parentName || !batchNames.has(parentName) || emittedNames.has(parentName);
    });

    if (readyIndex < 0) {
      throw new Error('Sub-place suggestions contain a parent cycle.');
    }

    const [entry] = pending.splice(readyIndex, 1);
    ordered.push(entry);
    emittedNames.add(normalizePlaceSuggestionName(entry?.suggestion?.name));
  }

  return ordered;
};

export const createDraftToSavedPlaceIdMap = (suggestions) =>
  new Map(
    (Array.isArray(suggestions) ? suggestions : [])
      .map((suggestion) => [
        String(suggestion?.draftPlaceId ?? '').trim(),
        String(suggestion?.savedPlaceId ?? '').trim(),
      ])
      .filter(([draftPlaceId, savedPlaceId]) => draftPlaceId && savedPlaceId)
  );

export const mergeInhabitantSuggestionBatches = (
  currentSuggestions,
  nextSuggestions,
  { replace = false, populationTranche = '' } = {}
) => {
  const current = Array.isArray(currentSuggestions) ? currentSuggestions : [];
  const next = Array.isArray(nextSuggestions) ? nextSuggestions : [];
  const normalizedTranche = normalizePopulationTranche(populationTranche);

  if (!replace) {
    return [...current, ...next];
  }

  return normalizedTranche
    ? [
        ...current.filter(
          (suggestion) => normalizePopulationTranche(suggestion?.populationTranche) !== normalizedTranche
        ),
        ...next,
      ]
    : next;
};

export const selectCurrentResidentDraftsForRequest = (
  currentSuggestions,
  { replace = false, populationTranche = '' } = {}
) => {
  const current = Array.isArray(currentSuggestions) ? currentSuggestions : [];
  const normalizedTranche = normalizePopulationTranche(populationTranche);

  if (!replace) {
    return current;
  }

  return normalizedTranche
    ? current.filter(
        (suggestion) => normalizePopulationTranche(suggestion?.populationTranche) !== normalizedTranche
      )
    : [];
};

export const remapInhabitantSuggestionPlaceIds = (suggestion, draftToSavedPlaceId) => {
  const resolvePlaceId = (draftPlaceId) => {
    const normalizedDraftPlaceId = String(draftPlaceId ?? '').trim();
    const savedPlaceId = draftToSavedPlaceId.get(normalizedDraftPlaceId);

    if (!savedPlaceId) {
      throw new Error(`Sub-place ${normalizedDraftPlaceId || '(missing)'} has not been saved.`);
    }

    return savedPlaceId;
  };

  return {
    ...suggestion,
    memberOfPlaceId: resolvePlaceId(suggestion?.memberOfPlaceId),
    placeRelationships: (Array.isArray(suggestion?.placeRelationships)
      ? suggestion.placeRelationships
      : [])
      .map((relationship) => ({
        ...relationship,
        targetPlaceId: resolvePlaceId(relationship?.targetPlaceId),
      })),
  };
};
